import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import withStyles from 'react-jss';
import { useSelector } from 'react-redux';
import { Layout, Menu, Drawer } from 'antd';
import { Link, useHistory } from 'react-router-dom';
import styles from './styles';
import { Modules, getDefaultSelectedKeys, getSubModules } from './helper';
import { get } from '../utils';

const { Sider } = Layout;
const { SubMenu } = Menu;

const Sidebar = props => {
  const { classes, collapsed, visible, setVisible } = props;

  const auth = useSelector(state => state.auth);

  const fabricTransferRequestCount = useSelector(
    state => state.fabricTransferRequestCount
  );

  const accessoryTransferRequestCount = useSelector(
    state => state.accessoryTransferRequestCount
  );

  const pendingRPOCount = useSelector(state => state.pendingRPOCount);

  const history = useHistory();

  const permissions = useMemo(() => get(auth, ['permissions'], []), [auth]);
  const role = useMemo(() => get(auth, ['user', 'role'], []), [auth]);
  // 'loading' | 'ready' | 'down' — drives the Notify menu group's placeholder
  // state (set at login by the notify-access fetch). Undefined before login.
  const notifyStatus = useMemo(() => get(auth, ['notifyStatus']), [auth]);

  const toggleDrawerCollapse = useCallback(() => {
    setVisible(!visible);
  }, [visible]);

  const renderItem = useCallback(
    ({ type, module, title, key, link, subModules, icon }) => {
      if (type === 'item') {
        if (role !== 'Owner' && !permissions.includes(module)) {
          return null;
        }
        return (
          <Menu.Item
            key={key}
            onClick={() => {
              history.push(link);
            }}
            icon={icon}
          >
            {title}
          </Menu.Item>
        );
      }
      if (type === 'list') {
        // The "Performance & Deadlines" group is driven by Notify's async
        // status (fetched at login), not static ERP perms. Show it immediately
        // with a loading / unavailable placeholder so it never looks missing;
        // once 'ready' it falls through to the normal perm-gated rendering
        // below. Owner always sees the real items. Fenced to this one group by
        // key — every other menu behaves exactly as before.
        if (
          key === 'Performance & Deadlines' &&
          role !== 'Owner' &&
          (notifyStatus === 'loading' || notifyStatus === 'down')
        ) {
          return (
            <SubMenu key={key} title={title} icon={icon}>
              <Menu.Item key={`${key}__status`} disabled>
                {notifyStatus === 'loading' ? 'Loading…' : 'Unavailable'}
              </Menu.Item>
            </SubMenu>
          );
        }
        if (
          role !== 'Owner' &&
          !getSubModules(subModules).filter(subModule =>
            permissions.includes(subModule)
          ).length
        ) {
          return null;
        }
        // if (title === 'Fabric Inventory') {
        //   return (
        //     <SubMenu
        //       key={key}
        //       title={
        //         <>
        //           {fabricTransferRequestCount !== 0 ? (
        //             <>
        //               <span style={{ color: '#FFBD33' }}>
        //                 ({fabricTransferRequestCount})
        //               </span>
        //               &nbsp;
        //             </>
        //           ) : (
        //             <>
        //               <span>({fabricTransferRequestCount})</span>
        //               &nbsp;
        //             </>
        //           )}
        //           {title}
        //         </>
        //       }
        //       icon={icon}
        //     >
        //       {subModules.map(subModule => renderItem(subModule))}
        //     </SubMenu>
        //   );
        // }
        // if (title === 'Accessory Inventory') {
        //   return (
        //     <SubMenu
        //       key={key}
        //       title={
        //         <>
        //           {accessoryTransferRequestCount !== 0 ? (
        //             <>
        //               <span style={{ color: '#FFBD33' }}>
        //                 ({accessoryTransferRequestCount})
        //               </span>
        //               &nbsp;
        //             </>
        //           ) : (
        //             <>
        //               <span>({accessoryTransferRequestCount})</span>&nbsp;
        //             </>
        //           )}
        //           {title}
        //         </>
        //       }
        //       icon={icon}
        //     >
        //       {subModules.map(subModule => renderItem(subModule))}
        //     </SubMenu>
        //   );
        // }
        // if (title === 'Purchase Order') {
        //   return (
        //     <SubMenu
        //       key={key}
        //       title={
        //         <>
        //           {pendingRPOCount !== 0 ? (
        //             <>
        //               <span style={{ color: '#FFBD33' }}>
        //                 ({pendingRPOCount})
        //               </span>
        //               &nbsp;
        //             </>
        //           ) : (
        //             <>
        //               <span>({pendingRPOCount})</span>
        //             </>
        //           )}
        //           {title}
        //         </>
        //       }
        //       icon={icon}
        //     >
        //       {subModules.map(subModule => renderItem(subModule))}
        //     </SubMenu>
        //   );
        // }
        return (
          <SubMenu key={key} title={title} icon={icon}>
            {subModules.map(subModule => renderItem(subModule))}
          </SubMenu>
        );
      }
    },
    // permissions/role MUST be deps: the Notify menu perms (notify:*) arrive
    // asynchronously from the login-time fetch. Without them here, renderItem
    // memoizes with the pre-fetch permissions and never re-renders the Notify
    // group once they merge in — invisible in dev (instant fetch) but broken in
    // prod where the ERP->Azure fetch takes seconds.
    [
      fabricTransferRequestCount,
      accessoryTransferRequestCount,
      pendingRPOCount,
      permissions,
      role,
      notifyStatus,
    ]
  );

  const defaultSelectedKeys = useMemo(
    () => [getDefaultSelectedKeys(history.location.pathname)],
    [history.location.pathname]
  );

  const menuItems = useMemo(() => {
    return auth.type && Modules.map(renderItem);
  }, [auth, renderItem]);

  return (
    <div>
      <Drawer
        className={classes.drawer}
        placement="left"
        closable={false}
        onClose={toggleDrawerCollapse}
        visible={visible}
      >
        <div className={classes.logoCenterDiv}>
          <Link to="/">
            <img src="/images/logo.png" alt="logo" className={classes.logo} />
          </Link>
        </div>
        <Menu
          theme="dark"
          mode="inline"
          className={classes.menu}
          defaultSelectedKeys={defaultSelectedKeys}
        >
          {menuItems}
        </Menu>
      </Drawer>
      <Sider className={classes.sidebar} collapsed={collapsed} width={230}>
        <div className={classes.logoCenterDiv}>
          <Link to="/">
            <img
              src="/images/logo.png"
              alt="logo"
              className={collapsed ? classes.logoCollapsed : classes.logo}
            />
          </Link>
        </div>
        <Menu
          theme="dark"
          mode="inline"
          className={classes.menu}
          defaultSelectedKeys={defaultSelectedKeys}
        >
          {menuItems}
        </Menu>
      </Sider>
    </div>
  );
};

Sidebar.propTypes = {
  classes: PropTypes.object.isRequired,
  collapsed: PropTypes.bool,
  visible: PropTypes.bool,
  setVisible: PropTypes.func.isRequired,
};

const StyledSidebar = withStyles(styles)(Sidebar);

export default StyledSidebar;
